/**
 * WorkspaceLinkDialog — shown when "Open in Cursor" fails with project_not_linked.
 * Lets the user provide the local git path, saves it, then retries workspace generation.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from '@/lib/api';
import { testIds } from '@/lib/test-ids';

interface WorkspaceLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName: string;
  basecampProjectId?: string | null;
  onLinked: () => void;
}

export function WorkspaceLinkDialog({
  open,
  onOpenChange,
  projectName,
  basecampProjectId,
  onLinked,
}: WorkspaceLinkDialogProps) {
  const [localGitPath, setLocalGitPath] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('main');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!localGitPath.trim()) {
      toast.error('Local git path is required');
      return;
    }
    setIsSaving(true);
    try {
      const res = await fetchWithAuth('/api/workspace/link-project', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          basecampProjectId: basecampProjectId || undefined,
          projectName,
          localGitPath: localGitPath.trim(),
          defaultBranch: defaultBranch.trim() || 'main',
        }),
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || `HTTP ${res.status}`);
      }
      toast.success('Project linked — retrying workspace generation…');
      onOpenChange(false);
      onLinked();
    } catch (err) {
      toast.error('Failed to link project', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={testIds.cursorWorkspaceMenu.linkDialog}>
        <DialogHeader>
          <DialogTitle>Link project to git repo</DialogTitle>
          <DialogDescription>
            <strong>{projectName}</strong> isn't linked to a local git repo yet.
            Provide the path so Maater can generate a Cursor workspace for it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="wld-git-path">Local git path</Label>
            <Input
              id="wld-git-path"
              placeholder="e.g. exida/exida-main"
              value={localGitPath}
              onChange={(e) => setLocalGitPath(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Relative to <code>/Users/rowan/hopwork/clients/</code>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="wld-branch">Default branch</Label>
            <Input
              id="wld-branch"
              placeholder="main"
              value={defaultBranch}
              onChange={(e) => setDefaultBranch(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !localGitPath.trim()}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Save &amp; open workspace
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
